import { useMemo, useState } from "react";
import { Plus, X, Route, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import MapView from "./MapView";
import { AddressInput } from "@/components/AddressInput";
import { RouteInsights, LegBreakdown } from "@/components/RouteInsights";
import { suggestOrder } from "@/lib/routeOrder";
import { newStop } from "@/data/options";
import api from "@/lib/api";
import { toast } from "sonner";

const inputCls =
  "focus-visible:ring-2 focus-visible:ring-[#F97316]/30 focus-visible:border-[#F97316]";

export default function RouteBuilder({
  pickup,
  setPickup,
  stops,
  setStops,
  routeData,
  setRouteData,
  manualMiles,
  setManualMiles,
  totalRouteHours = 0,
}) {
  const [loading, setLoading] = useState(false);
  const [previewPins, setPreviewPins] = useState({}); // key (pickup|stopId) -> {lat, lon, label}

  const setPin = (key, s) =>
    setPreviewPins((prev) => (s.lat != null ? { ...prev, [key]: { lat: s.lat, lon: s.lon, label: s.label } } : prev));
  const clearPin = (key) =>
    setPreviewPins((prev) => {
      const { [key]: _, ...rest } = prev;
      return rest;
    });

  const updateStop = (id, v) => setStops(stops.map((s) => (s.id === id ? { ...s, value: v } : s)));
  const updateStopCustomer = (id, v) => setStops(stops.map((s) => (s.id === id ? { ...s, customer: v } : s)));
  const addStop = () => setStops([...stops, newStop()]);
  const removeStop = (id) => {
    clearPin(id);
    const removed = stops.find((s) => s.id === id);
    // A calculated route no longer matches once a filled stop is removed — clear stale data.
    if (routeData && removed?.value.trim()) setRouteData(null);
    setStops(stops.length > 1 ? stops.filter((s) => s.id !== id) : [newStop()]);
  };

  const calcRoute = async (stopList = stops) => {
    const validStops = stopList.map((s) => s.value).filter((s) => s.trim());
    if (!pickup.trim() || validStops.length === 0) {
      toast.error("Enter a pickup and at least one delivery stop");
      return;
    }
    setLoading(true);
    try {
      const { data } = await api.post("/route", { pickup, stops: validStops });
      setRouteData(data);
      setManualMiles("");
      setPreviewPins({});
      toast.success(`Route: ${data.total_miles} mi round trip`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not compute route");
    } finally {
      setLoading(false);
    }
  };

  // Suggest a better drop order: greedy nearest-neighbor from the pickup.
  const orderSuggestion = useMemo(() => suggestOrder(routeData?.points), [routeData]);

  const applySuggestedOrder = () => {
    if (!orderSuggestion) return;
    const validStops = stops.filter((s) => s.value.trim());
    const others = stops.filter((s) => !s.value.trim());
    const reordered = [...orderSuggestion.map((i) => validStops[i]).filter(Boolean), ...others];
    setStops(reordered);
    toast.success("Stops reordered — recalculating…");
    calcRoute(reordered);
  };

  const displayMiles = manualMiles !== "" ? Number(manualMiles) : routeData?.total_miles;
  const driveTime = routeData
    ? `${Math.floor(routeData.total_minutes / 60)}h ${routeData.total_minutes % 60}m`
    : "—";

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
      {/* Left: address builder */}
      <div className="bg-white border border-border rounded-lg shadow-sm p-5 space-y-4">
        <div>
          <Label className="text-xs font-bold uppercase tracking-[0.1em] text-[#57534E]">
            Pickup Address
          </Label>
          <div className="mt-1.5">
            <AddressInput
              value={pickup}
              onChange={setPickup}
              onPick={(s) => setPin("pickup", s)}
              icon
              className={`pl-9 ${inputCls}`}
              data-testid="pickup-input"
            />
          </div>
        </div>

        <div className="space-y-2">
          <Label className="text-xs font-bold uppercase tracking-[0.1em] text-[#57534E]">
            Delivery Stops
          </Label>
          {stops.map((s, i) => (
            <div key={s.id} className="space-y-1">
              <div className="flex items-center gap-2">
                <div className="h-6 w-6 shrink-0 rounded-full bg-[#F97316] text-white text-xs font-bold flex items-center justify-center">
                  {i + 1}
                </div>
                <AddressInput
                  value={s.value}
                  onChange={(v) => updateStop(s.id, v)}
                  onPick={(sg) => setPin(s.id, sg)}
                  placeholder={`Stop ${i + 1} address`}
                  className={inputCls}
                  data-testid={`stop-input-${i}`}
                />
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => removeStop(s.id)}
                  className="shrink-0 text-[#57534E] hover:text-red-600"
                  data-testid={`remove-stop-${i}`}
                >
                  <X className="h-4 w-4" />
                </Button>
              </div>
              {stops.filter((x) => x.value.trim()).length > 1 && s.value.trim() && (
                <div className="pl-8 pr-10">
                  <Input
                    value={s.customer || ""}
                    onChange={(e) => updateStopCustomer(s.id, e.target.value)}
                    placeholder={`Customer at stop ${i + 1} (for delivery split)`}
                    className={`h-8 text-xs ${inputCls}`}
                    data-testid={`stop-customer-${i}`}
                  />
                </div>
              )}
            </div>
          ))}
          <Button
            variant="outline"
            size="sm"
            onClick={addStop}
            className="border-dashed border-[#1C1917]/30"
            data-testid="add-stop-button"
          >
            <Plus className="h-4 w-4 mr-1" /> Add stop
          </Button>
        </div>

        <Button
          onClick={() => calcRoute()}
          disabled={loading}
          className="w-full bg-[#1C1917] hover:bg-black text-white font-bold"
          data-testid="calculate-route-button"
        >
          {loading ? (
            <Loader2 className="h-4 w-4 mr-2 animate-spin" />
          ) : (
            <Route className="h-4 w-4 mr-2" />
          )}
          Calculate Route
        </Button>

        <RouteInsights
          routeData={routeData}
          orderSuggestion={orderSuggestion}
          onApplyOrder={applySuggestedOrder}
          loading={loading}
          totalRouteHours={totalRouteHours}
          displayMiles={displayMiles}
          driveTime={driveTime}
        />

        <div>
          <Label className="text-xs font-bold uppercase tracking-[0.1em] text-[#57534E]">
            Manual Mileage Override
          </Label>
          <Input
            type="number"
            value={manualMiles}
            onChange={(e) => setManualMiles(e.target.value)}
            placeholder="Leave blank to use calculated"
            className={`mt-1.5 ${inputCls}`}
            data-testid="manual-miles-input"
          />
        </div>

        <LegBreakdown legs={routeData?.legs} />
      </div>

      {/* Right: map */}
      <div className="bg-white border border-border rounded-lg shadow-sm overflow-hidden min-h-[420px] lg:min-h-0">
        <MapView
          points={routeData?.points}
          geometry={routeData?.geometry}
          previewPoints={Object.values(previewPins)}
        />
      </div>
    </div>
  );
}
